import { redirect } from "@tanstack/react-router";
import { z } from "zod";
import { AppError, authError } from "./app-error";
import { authClient } from "./auth-client";
import { destinationAfterSignIn } from "./pending-invitation";

export type Session = typeof authClient.$Infer.Session;

/**
 * What a route may conclude about the visitor before it renders anything.
 *
 * `unverified` is its own answer rather than a kind of signed-in: Better Auth still returns a
 * session for an address that was never proven, and every use of it is refused, so the only
 * screen it can open is the one that proves the address.
 */
export type SessionCheck =
  | { status: "signed-in"; session: Session }
  | { status: "unverified"; session: Session }
  | { status: "signed-out" }
  | { status: "failed"; error: AppError };

type SessionState = {
  data: unknown;
  error: unknown;
  isPending: boolean;
  isRefetching: boolean;
  refetch: () => Promise<void>;
};

/** The same store `authClient.useSession()` reads, so a route check and the page share one fetch. */
const sessionStore = authClient.$store.atoms["session"] as {
  get: () => SessionState;
  subscribe: (listener: (state: SessionState) => void) => () => void;
};

/**
 * The fields a signed-in screen reads, and nothing short of them is a session, however truthy. A
 * misrouted `/api/auth` answers with the app's own `index.html` and a 200, which the SDK hands
 * back as a string; a partial object would get further and fail inside whichever page first read
 * the missing field. Either is an unreadable answer, shown as one, never a screen that crashes.
 */
const sessionShape = z.object({
  user: z.object({
    id: z.string().min(1),
    email: z.string(),
    name: z.string(),
    emailVerified: z.boolean(),
  }),
  session: z.object({ id: z.string().min(1) }),
});

function isSession(value: unknown): value is Session {
  return sessionShape.safeParse(value).success;
}

/** Who the store holds as signed in right now, if anyone: the id alone, never a reason to trust it. */
export function signedInUserId(): string | null {
  const { data } = sessionStore.get();
  return isSession(data) ? data.user.id : null;
}

/**
 * Tells `listener` whenever the signed-in person changes, to someone else or to no one. For work
 * that began as one person and must not finish as another.
 */
export function watchSignedInUser(listener: (userId: string | null) => void): () => void {
  let last = signedInUserId();
  return sessionStore.subscribe(() => {
    const now = signedInUserId();
    if (now === last) return;
    last = now;
    listener(now);
  });
}

/** Resolves once the store holds a usable session or has finished asking for one. */
function settled(): Promise<SessionState> {
  return new Promise((resolve) => {
    let done = false;
    let stop: (() => void) | undefined;
    // Subscribing mounts the store, which is what starts the SDK's first read on a cold load.
    stop = sessionStore.subscribe((state) => {
      if (done || !(isSession(state.data) || (!state.isPending && !state.isRefetching))) return;
      done = true;
      resolve(state);
      // The first call is synchronous, before `subscribe` has returned its `stop`.
      if (stop) stop();
      else queueMicrotask(() => stop?.());
    });
  });
}

function classify({ data, error }: SessionState): SessionCheck {
  if (isSession(data)) {
    return data.user.emailVerified
      ? { status: "signed-in", session: data }
      : { status: "unverified", session: data };
  }
  if (data !== null && data !== undefined)
    return { status: "failed", error: new AppError("INVALID_RESPONSE") };
  // A refusal is an answer — nobody is signed in. Any other failure is not, and must never be
  // read as one: an outage would otherwise sign everybody out.
  if (!error || (error as { status?: unknown }).status === 401) return { status: "signed-out" };
  return { status: "failed", error: authError(error, "session") };
}

/**
 * For screens anyone may open: the answer the store already has, waiting only when it has none
 * yet. A stale "signed out" costs nothing here, because the screen is open to everybody anyway.
 */
export async function readSession(): Promise<SessionCheck> {
  return classify(await settled());
}

/**
 * For screens that require a session. A usable session is trusted as held; an idle store without
 * one is asked again before anyone is turned away, because that answer can predate a sign-in that
 * has just finished — the SDK only refreshes its store 10ms after one, and the sign-in screen
 * navigates here straight away. A read that is already in flight is waited on, never repeated.
 */
export async function confirmSession(): Promise<SessionCheck> {
  const current = sessionStore.get();
  if (!isSession(current.data) && !current.isPending && !current.isRefetching) {
    await current.refetch();
  }
  return classify(await settled());
}

/**
 * `beforeLoad` for the screens that get someone in: a finished session has nothing to do on them
 * and goes home before the form is drawn. Only a finished one — an unproven session still needs
 * these screens, since signing in again is what sends it a fresh code.
 */
export async function leaveIfSignedIn() {
  if ((await readSession()).status === "signed-in") {
    throw redirect({ ...destinationAfterSignIn(), replace: true });
  }
}
